import { useCallback, useRef } from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { CHAT_BOTTOM_THRESHOLD } from '../constants';
import type { TChatMessagesListRef, TUseChatScrollFollowParams } from '../types';

export const useChatScrollFollow = ({ forwardedRef, props }: TUseChatScrollFollowParams) => {
    const { onScroll, onScrollEndDrag, onMomentumScrollEnd, onContentSizeChange } = props;
    const listRef = useRef<TChatMessagesListRef | null>(null);
    const atBottomRef = useRef(true);
    const contentHeightRef = useRef(0);

    const ref = useCallback(
        (instance: TChatMessagesListRef | null) => {
            listRef.current = instance;
            if (typeof forwardedRef === 'function') forwardedRef(instance);
            else if (forwardedRef) forwardedRef.current = instance;
        },
        [forwardedRef],
    );

    const updateAtBottom = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        atBottomRef.current = event.nativeEvent.contentOffset.y <= CHAT_BOTTOM_THRESHOLD;
    };

    return {
        ref,
        onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
            if (event.nativeEvent.contentOffset.y <= CHAT_BOTTOM_THRESHOLD) atBottomRef.current = true;
            onScroll?.(event);
        },
        onScrollEndDrag: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
            updateAtBottom(event);
            onScrollEndDrag?.(event);
        },
        onMomentumScrollEnd: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
            updateAtBottom(event);
            onMomentumScrollEnd?.(event);
        },
        onContentSizeChange: (width: number, height: number) => {
            const grew = contentHeightRef.current > 0 && height > contentHeightRef.current;
            contentHeightRef.current = height;
            if (grew && atBottomRef.current) {
                listRef.current?.scrollToOffset({ offset: 0, animated: true });
            }
            onContentSizeChange?.(width, height);
        },
    };
};
